/**
 * Batch get USDC balances and reserve data for multiple markets
 *
 * Uses viem's multicall to fetch all data in minimal RPC calls,
 * significantly more reliable than individual readContract calls.
 */
import type { Address, MulticallParameters, MulticallReturnType } from "viem"
import { erc20Abi } from "viem"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import { fromUsdcScale } from "../../internal/convert"

/**
 * Client type that supports both multicall and readContract.
 * Accepts any client with a multicall method (e.g., PublicClient).
 */
type MulticallClient = {
	multicall: <const contracts extends readonly unknown[], allowFailure extends boolean = true>(
		args: MulticallParameters<contracts, allowFailure>,
	) => Promise<MulticallReturnType<contracts, allowFailure>>
}

export interface GetMarketBalancesBatchParams {
	markets: Address[]
}

export interface MarketBalanceInfo {
	/** USDC balance held by the market contract (human-readable) */
	balance: number
	/** Raw USDC balance (contract scale) */
	balanceRaw: bigint
	/** Unswept protocol fees (human-readable) */
	accruedProtocolFees: number
	/** Total payouts already claimed by winners (human-readable) */
	totalClaimedPayouts: number
}

export interface GetMarketBalancesBatchResult {
	/** Map of lowercase market address to balance info */
	balances: Map<string, MarketBalanceInfo>
	/** Markets that failed to fetch (with error message) */
	errors: Map<string, string>
}

/**
 * Get USDC balances and reserve data for multiple markets in batched RPC calls.
 *
 * Uses 2 multicall RPCs:
 * 1. First: reads usdc() address + accruedProtocolFees + totalClaimedPayouts
 * 2. Second: reads balanceOf for each market on the USDC token
 *
 * This is significantly more reliable than individual readContract calls,
 * especially on testnets or rate-limited RPCs.
 *
 * NOTE: Results are stored with lowercase addresses as keys for consistent lookups.
 *
 * @example
 * ```ts
 * import { getMarketBalancesBatch } from "@breakout/sdk/actions/read"
 *
 * const { balances, errors } = await getMarketBalancesBatch(publicClient, {
 *   markets: ["0x...", "0x...", "0x..."],
 * })
 *
 * for (const [market, info] of balances) {
 *   console.log(`${market}: $${info.balance.toFixed(2)} USDC`)
 * }
 * ```
 */
export async function getMarketBalancesBatch(
	client: MulticallClient,
	params: GetMarketBalancesBatchParams,
): Promise<GetMarketBalancesBatchResult> {
	if (params.markets.length === 0) {
		return { balances: new Map(), errors: new Map() }
	}

	// Step 1: Multicall to get USDC address + accruedProtocolFees + totalClaimedPayouts
	const firstMarket = params.markets[0]
	if (!firstMarket) {
		return { balances: new Map(), errors: new Map() }
	}

	const step1Contracts = [
		// Get USDC token address from first market (same for all markets in a week)
		{
			address: firstMarket,
			abi: breakoutMarketAbi,
			functionName: "usdc" as const,
		},
		// accruedProtocolFees for each market
		...params.markets.map(market => ({
			address: market,
			abi: breakoutMarketAbi,
			functionName: "accruedProtocolFees" as const,
		})),
		// totalClaimedPayouts for each market
		...params.markets.map(market => ({
			address: market,
			abi: breakoutMarketAbi,
			functionName: "totalClaimedPayouts" as const,
		})),
	]

	const step1Results = await client.multicall({
		contracts: step1Contracts,
		allowFailure: true,
	})

	// Extract USDC address from first result
	const usdcResult = step1Results[0]
	if (!usdcResult || usdcResult.status !== "success") {
		// If we can't get the USDC address, we can't get balances
		const errors = new Map<string, string>()
		for (const market of params.markets) {
			errors.set(market.toLowerCase(), "Failed to read USDC token address from market")
		}
		return { balances: new Map(), errors }
	}

	const usdcAddress = usdcResult.result as Address

	// Parse fees and claimed payouts from step 1
	const marketCount = params.markets.length
	const feesStartIndex = 1
	const claimedStartIndex = 1 + marketCount

	const feesMap = new Map<string, bigint>()
	const claimedMap = new Map<string, bigint>()

	for (let i = 0; i < marketCount; i++) {
		const market = params.markets[i]
		if (!market) {
			continue
		}
		const normalized = market.toLowerCase()

		const feesResult = step1Results[feesStartIndex + i]
		if (feesResult?.status === "success") {
			feesMap.set(normalized, feesResult.result as bigint)
		}

		const claimedResult = step1Results[claimedStartIndex + i]
		if (claimedResult?.status === "success") {
			claimedMap.set(normalized, claimedResult.result as bigint)
		}
	}

	// Step 2: Multicall balanceOf on the USDC token for each market
	const balanceContracts = params.markets.map(market => ({
		address: usdcAddress,
		abi: erc20Abi,
		functionName: "balanceOf" as const,
		args: [market] as const,
	}))

	const balanceResults = await client.multicall({
		contracts: balanceContracts,
		allowFailure: true,
	})

	// Build final result
	const balances = new Map<string, MarketBalanceInfo>()
	const errors = new Map<string, string>()

	for (let i = 0; i < marketCount; i++) {
		const market = params.markets[i]
		if (!market) {
			continue
		}

		const normalized = market.toLowerCase()
		const balanceResult = balanceResults[i]

		if (balanceResult?.status === "success") {
			const balanceRaw = balanceResult.result as bigint
			const fees = feesMap.get(normalized) ?? 0n
			const claimed = claimedMap.get(normalized) ?? 0n

			balances.set(normalized, {
				balance: fromUsdcScale(balanceRaw),
				balanceRaw,
				accruedProtocolFees: fromUsdcScale(fees),
				totalClaimedPayouts: fromUsdcScale(claimed),
			})
		} else {
			const errorMsg =
				balanceResult?.status === "failure" ? balanceResult.error.message : "Unknown error"
			errors.set(normalized, errorMsg)
		}
	}

	return { balances, errors }
}
